import { useGetList, useListContext, useTranslate } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";

import { ChoiceChips } from "../design/ChoiceChips";
import { formatMxn } from "../quoter/PlanSchedule";
import type { ReceivableStatus, ReceivableSummary } from "./invoicingTypes";
import {
  RECEIVABLE_FILTER_STATUSES,
  sumTotalsByStatus,
} from "./invoicingScreenRules";

// Totals read one bounded page (the list itself paginates server-side).
const TOTALS_PAGE_SIZE = 1000;

/** Totals per status of the rows matching the search (the status chips do not narrow them). */
const StatusTotals = ({ search }: { search?: string }) => {
  const translate = useTranslate();
  const { data } = useGetList<ReceivableSummary>("receivables", {
    filter: search ? { q: search } : {},
    pagination: { page: 1, perPage: TOTALS_PAGE_SIZE },
    sort: { field: "created_at", order: "DESC" },
  });
  const totals = sumTotalsByStatus(data ?? []);
  return (
    <dl
      className="grid grid-cols-2 gap-3 md:grid-cols-4"
      aria-label={translate("crm.invoicing.receivables.totals")}
    >
      {RECEIVABLE_FILTER_STATUSES.map((status) => (
        <div
          key={status}
          data-testid={`total-${status}`}
          className="rounded-lg border bg-card p-3"
        >
          <dt className="text-xs text-muted-foreground">
            {translate(`crm.invoicing.status.receivable.${status}`)}
          </dt>
          <dd className="text-base font-semibold tabular-nums">
            {formatMxn(totals[status])}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const StatusChips = () => {
  const translate = useTranslate();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const status = filterValues.status as ReceivableStatus | undefined;
  return (
    <ChoiceChips
      label={translate("crm.invoicing.receivables.status_filter")}
      mode="single"
      value={status ? [status] : []}
      options={RECEIVABLE_FILTER_STATUSES.map((id) => ({
        id,
        label: translate(`crm.invoicing.status.receivable.${id}`),
      }))}
      onChange={([next]) => {
        const rest = { ...filterValues };
        delete rest.status;
        setFilters(next ? { ...rest, status: next } : rest, displayedFilters);
      }}
    />
  );
};

const ReceivableListBody = () => {
  const translate = useTranslate();
  const { filterValues } = useListContext();
  return (
    <div className="space-y-4">
      <StatusTotals search={filterValues.q as string | undefined} />
      <StatusChips />
      <DataTable rowClick="show" bulkActionButtons={false}>
        <DataTable.Col
          source="operation_id"
          label="crm.invoicing.fields.operation"
        />
        <DataTable.Col
          source="project_name"
          label="crm.invoicing.fields.project"
          render={(record: ReceivableSummary) => record.project_name ?? "—"}
        />
        <DataTable.Col
          source="unit_identifier"
          label="crm.invoicing.fields.unit"
          render={(record: ReceivableSummary) => record.unit_identifier ?? "—"}
        />
        <DataTable.Col
          source="client_name"
          label="crm.invoicing.fields.client"
          render={(record: ReceivableSummary) => record.client_name ?? "—"}
        />
        <DataTable.Col
          source="advisor_name"
          label="crm.invoicing.fields.advisor"
          render={(record: ReceivableSummary) => record.advisor_name ?? "—"}
        />
        <DataTable.Col
          source="installment_number"
          label="crm.invoicing.fields.installment"
        />
        <DataTable.Col
          source="status"
          label="crm.invoicing.fields.status"
          render={(record: ReceivableSummary) =>
            translate(`crm.invoicing.status.receivable.${record.status}`)
          }
        />
        <DataTable.Col
          source="total_amount"
          label="crm.invoicing.fields.total"
          className="text-right tabular-nums"
          render={(record: ReceivableSummary) => formatMxn(record.total_amount)}
        />
      </DataTable>
    </div>
  );
};

/**
 * Almikia Epic E6 (TASK-040): "Cuentas por cobrar" over `receivables_summary`.
 * Reached by direccion, contabilidad and auditoria (read-only; the row actions
 * live on the detail page). Newest first, server-side pagination, search by
 * operation id / project / client, status chips and totals per status. It
 * reads only the text snapshots of the receivable: no operations or clients
 * data, no link into those screens.
 */
export const ReceivableList = () => {
  const translate = useTranslate();
  return (
    <List
      resource="receivables"
      sort={{ field: "created_at", order: "DESC" }}
      perPage={25}
      filters={[
        <SearchInput
          key="q"
          source="q"
          alwaysOn
          placeholder={translate("crm.invoicing.receivables.search")}
        />,
      ]}
      actions={false}
    >
      <ReceivableListBody />
    </List>
  );
};
